"use client";

import type { ComponentType } from "react";

import { ContactEntitySkeleton } from "./ContactEntity.Skeleton";
import type { ContactEntityProps } from "./ContactEntity.types";

/**
 * Thin preset wrappers for ContactEntity. Each preset fixes `layout`
 * plus a tight `actions` / `fields` object. Call sites should prefer
 * presets over the base component.
 */

type BasePreset = ComponentType<ContactEntityProps>;

export function createContactReportPreset(Base: BasePreset) {
  return function ContactEntityReport(props: ContactEntityProps) {
    return (
      <Base
        layout="card"
        {...props}
        actions={{ viewProfile: true, ...(props.actions ?? {}) }}
        fields={{
          tags: true,
          relevance: true,
          status: true,
          email: false,
          linkedIn: false,
          location: false,
          ...(props.fields ?? {}),
        }}
      />
    );
  };
}

export function createContactLibraryPreset(Base: BasePreset) {
  return function ContactEntityLibrary(props: ContactEntityProps) {
    return (
      <Base
        layout="card"
        {...props}
        actions={{
          selection: true,
          statusPicker: true,
          favorite: true,
          library: true,
          quickView: true,
          reachOut: true,
          ...(props.actions ?? {}),
        }}
        fields={{
          email: true,
          linkedIn: true,
          location: true,
          tags: true,
          relevance: true,
          status: true,
          ...(props.fields ?? {}),
        }}
      />
    );
  };
}

export function createContactPreviewPreset(Base: BasePreset) {
  return function ContactEntityPreview(props: ContactEntityProps) {
    return (
      <Base
        layout="card"
        {...props}
        actions={{
          statusPicker: true,
          library: true,
          quickView: true,
          reachOut: true,
          ...(props.actions ?? {}),
        }}
        fields={{
          email: true,
          linkedIn: true,
          location: false,
          tags: false,
          relevance: false,
          status: false,
          ...(props.fields ?? {}),
        }}
      />
    );
  };
}

export function createContactRowPreset(Base: BasePreset) {
  return function ContactEntityRow(props: ContactEntityProps) {
    return (
      <Base
        layout="row"
        {...props}
        actions={{
          statusPicker: true,
          viewProfile: true,
          reachOut: true,
          ...(props.actions ?? {}),
        }}
      />
    );
  };
}

export interface ContactEntityPresetsAttached {
  Report: ReturnType<typeof createContactReportPreset>;
  Library: ReturnType<typeof createContactLibraryPreset>;
  Preview: ReturnType<typeof createContactPreviewPreset>;
  Row: ReturnType<typeof createContactRowPreset>;
  Skeleton: typeof ContactEntitySkeleton;
}

export function attachContactEntityPresets<T extends BasePreset>(Base: T) {
  const extended = Base as T & ContactEntityPresetsAttached;
  extended.Report = createContactReportPreset(Base);
  extended.Library = createContactLibraryPreset(Base);
  extended.Preview = createContactPreviewPreset(Base);
  extended.Row = createContactRowPreset(Base);
  extended.Skeleton = ContactEntitySkeleton;
  return extended;
}
